import {
  CurrencyISOCode,
  FetchPricing,
  PriceCalculationRequest,
  PricingPeriod,
  usePricing
} from '@atlassiansox/bxp-use-pricing';
import { useMemo } from 'react';
import { ProductPlan } from '../models';
import { usePriceFinderStore } from '../store';
import { DefaultPlanLimits, SpecialPlanNames } from '../utils';

const getPriceFinderRequests = (
  pricingExampleUnitCounts: number[],
  productKey: string,
  productPlans: ProductPlan[],
  unitMinEnterprise: number,
  unitCount: number,
  billingCycle: PricingPeriod
): PriceCalculationRequest[] => {
  return useMemo(() => {
    const getNumUnits = (planName: string, signupUnitLimit: number) => {
      let adjNumUnits = 0;

      // following scenarios do not have pricing and should send a zero unit count,
      // the zero pricing will not be displayed and is needed only to retain the
      // correct array order of the pricing requests/responses

      // Free plan has no pricing
      const isFree = planName === SpecialPlanNames.FREE;

      // Enterprise plans do not have monthly pricing
      const isMonthlyEnterprise =
        planName === SpecialPlanNames.ENTERPRISE && billingCycle === 'MONTHLY';

      // Enterprise plans have a minimum unit count
      const isUnmetUnitCountEnterprise =
        planName === SpecialPlanNames.ENTERPRISE &&
        unitCount < unitMinEnterprise;
      const isOverSignupUnitLimit = unitCount > signupUnitLimit;

      if (
        ![
          isFree,
          isMonthlyEnterprise,
          isUnmetUnitCountEnterprise,
          isOverSignupUnitLimit
        ].includes(true)
      ) {
        adjNumUnits = unitCount;
      }

      // keep unit count to general maximum limit
      return Math.min(adjNumUnits, DefaultPlanLimits.UNIT_LIMIT);
    };

    const planPriceReqs: PriceCalculationRequest[] = productPlans.map(
      ({ offeringSku, planName, signupUnitLimit }) => ({
        ccpOfferingSKU: offeringSku,
        hamsProductKey:
          productKey +
          (planName === SpecialPlanNames.STANDARD
            ? ''
            : `.${planName.toLowerCase()}`),
        numUnits: getNumUnits(
          planName,
          signupUnitLimit || DefaultPlanLimits.UNIT_LIMIT
        ),
        period: billingCycle
      })
    );

    const standardProductPlan = productPlans.find(
      (plan) => plan.planName === SpecialPlanNames.STANDARD
    );

    if (!standardProductPlan) {
      throw new Error(
        'Standard product plan was not provided as required for pricing example.'
      );
    }

    const pricingExampleRequest: PriceCalculationRequest = {
      ccpOfferingSKU: standardProductPlan.offeringSku,
      hamsProductKey: productKey,
      numUnits:
        pricingExampleUnitCounts[billingCycle === 'MONTHLY' ? 0 : 1] || 300,
      period: billingCycle
    };

    return planPriceReqs.concat(pricingExampleRequest);
  }, [unitCount, billingCycle]);
};

export function usePriceFinderPricingData(
  pricingExampleUnitCounts: number[],
  productKey: string,
  productPlans: ProductPlan[],
  currencyISOCode: CurrencyISOCode,
  localeISOCode: string,
  fetchPricing: FetchPricing,
  unitMinEnterprise: number
) {
  const [{ unitCount, billingCycle }] = usePriceFinderStore();
  const priceCalcReqs = getPriceFinderRequests(
    pricingExampleUnitCounts,
    productKey,
    productPlans,
    unitMinEnterprise,
    unitCount,
    billingCycle
  );

  return usePricing(
    priceCalcReqs,
    fetchPricing,
    currencyISOCode,
    localeISOCode
  );
}
